import React, { forwardRef } from 'react';
import Button, { type IButtonProps } from '@components/ui/Button/Button';
import { formatCurrency } from '@utils/formatters';
import styles from './BalanceCard.module.css';

export interface IBalanceCardAction extends Omit<IButtonProps, 'children' | 'onClick'> {
  label: string;
  onClick: () => void;
}

export interface IBalanceCardProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  amount: number;
  title?: string;
  subtitle?: string;
  accountType?: string;
  /**
   * vertical: the title row, with any actions, above the amount.
   * horizontal: the amount and subtitle beside the actions; no title.
   * split: the title and amount share the top row, with the actions below.
   */
  layout?: 'vertical' | 'horizontal' | 'split';
  /** Each renders as an icon Button; the rest of the action is passed to it. */
  actions?: IBalanceCardAction[];
  as?: 'div' | 'section';
  titleAs?: 'p' | 'h2';
  amountAs?: 'p' | 'div' | 'h1';
  /** Replaces the default title type styles. */
  titleClassName?: string;
  /** Replaces the default amount type styles. */
  amountClassName?: string;
  headerClassName?: string;
  actionsClassName?: string;
}

const BalanceCard = forwardRef<HTMLElement, IBalanceCardProps>(
  (
    {
      amount,
      title = 'Your Balance',
      subtitle,
      accountType,
      layout = 'vertical',
      actions,
      as = 'div',
      titleAs = 'p',
      amountAs = 'p',
      titleClassName,
      amountClassName,
      headerClassName,
      actionsClassName,
      className,
      ...rest
    },
    ref,
  ) => {
    const Root = as as 'div';
    const Title = titleAs as 'p';
    const Amount = amountAs as 'p';
    const join = (...classes: Array<string | false | undefined>) =>
      classes.filter(Boolean).join(' ');

    const titleNode = <Title className={titleClassName ?? styles.title}>{title}</Title>;
    const amountNode = (
      <Amount className={amountClassName ?? styles.amount}>{formatCurrency(amount)}</Amount>
    );
    const subtitleRow = (subtitle || accountType) && (
      <div className={styles.subtitleRow}>
        {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        {accountType && <span className={styles.accountType}>{accountType}</span>}
      </div>
    );
    const actionsNode = actions && actions.length > 0 && (
      <div className={join(styles.actions, actionsClassName)}>
        {actions.map(({ label, onClick, ...buttonProps }) => (
          <Button key={label} variant="icon" size="md" {...buttonProps} onClick={onClick}>
            {label}
          </Button>
        ))}
      </div>
    );
    const header = (children: React.ReactNode) => (
      <div className={join(styles.topRow, headerClassName)}>{children}</div>
    );

    return (
      <Root
        ref={ref as React.Ref<HTMLDivElement>}
        className={join(styles.card, styles[layout], className)}
        {...rest}
      >
        {layout === 'horizontal' && (
          <>
            <div className={styles.balanceInfo}>
              {amountNode}
              {subtitleRow}
            </div>
            {actionsNode}
          </>
        )}
        {layout === 'vertical' && (
          <>
            {header(
              <>
                {titleNode}
                {actionsNode}
              </>,
            )}
            {amountNode}
            {subtitleRow}
          </>
        )}
        {layout === 'split' && (
          <>
            {header(
              <>
                {titleNode}
                {amountNode}
              </>,
            )}
            {subtitleRow}
            {actionsNode}
          </>
        )}
      </Root>
    );
  },
);

BalanceCard.displayName = 'BalanceCard';

export default BalanceCard;
